import React, { useMemo } from 'react';
import { Popover } from 'antd';
import moment from 'moment';
import {
  ApprovalUsersIcon,
  PlayerControlBarArrowRightIcon,
  CalendarV2Icon,
  PanelCalendarIcon
} from '@icons';
import { hashedColor } from '@helpers/hashedColor';
import type { AwStateStage } from '@components/Modals/ApprovalModal/ApprovalModal/ApprovalModal';
import StageMember from '@components/Modals/ApprovalModal/ApprovalModal/components/StageMember';

import './StagePreviewCard.less';

type StagePreviewCardProps = {
  stage: AwStateStage;
  ownerEmail: string;
  stageNumber: number;
};

export default function StagePreviewCard({
  stage,
  ownerEmail,
  stageNumber
}: StagePreviewCardProps) {
  const formattedDeadline = useMemo(() => {
    if (!stage.deadline) return null;
    const deadline = moment(stage.deadline);
    const time = deadline.format('LT');
    return {
      date: deadline.format('ll'),
      time: time !== '11:59 PM' ? time : null
    };
  }, [stage]);

  const stageColor = useMemo(
    () => hashedColor(`${stageNumber}`, 'taskGroup'),
    [stageNumber]
  );

  const { members, membersPopover } = useMemo(() => {
    return {
      members: stage.members.slice(0, 3),
      membersPopover: stage.members.slice(3)
    };
  }, [stage.members]);

  const stageCompleteConditionLabel = useMemo(() => {
    const withDecision = stage.members.filter((x) => x.canSubmitDecision);
    const withDominatingDecisions = withDecision.filter(
      (x) => x.hasDominatingDecision
    );
    const allDecisionsSubmittedChecked =
      stage.completionCondition === 'all_decisions_submitted' &&
      (withDominatingDecisions.length === 0 ||
        withDominatingDecisions.length === withDecision.length);
    const oneDecisionSubmittedChecked =
      stage.completionCondition === 'one_decision_submitted' &&
      withDominatingDecisions.length < 2;
    if (allDecisionsSubmittedChecked) return 'All decisions submitted';
    if (oneDecisionSubmittedChecked) return 'One decision submitted';
    if (stage.completionCondition === 'all_decisions_submitted')
      return 'Selected decisions submitted';
    return 'One of selected decisions submitted';
  }, [stage.members, stage.completionCondition]);

  return (
    <div className="stage-preview-card" style={{ borderColor: stageColor }}>
      <div
        className="stage-preview-card_arrow-line"
        style={{ backgroundColor: stageColor }}
      >
        <PlayerControlBarArrowRightIcon />
      </div>
      <div className="stage-preview-card_head">
        <div className="stage-preview-card_head-count">{stageNumber}</div>
        <div className="stage-preview-card_head-label">{stage.name}</div>
      </div>
      {!formattedDeadline && (
        <div className="stage-preview-card_deadline">
          No deadline <CalendarV2Icon />
        </div>
      )}
      {!!formattedDeadline && (
        <div className="stage-preview-card_deadline">
          {formattedDeadline.date}{' '}
          {formattedDeadline.time && (
            <div className="stage-preview-card_deadline-time">
              {formattedDeadline.time}
            </div>
          )}{' '}
          <PanelCalendarIcon />
        </div>
      )}
      <div className="stage-preview-card_text">
        Stage is complete when {stageCompleteConditionLabel}
      </div>
      {!!members.length && (
        <div className="stage-preview-card_members">
          {members.map((item) => (
            <div key={item.email} className="stage-preview-card_members-block">
              <StageMember
                member={item}
                isOwner={item.email === ownerEmail}
                isShowPriorityText={false}
              />
            </div>
          ))}
        </div>
      )}
      {!!membersPopover.length && (
        <Popover
          overlayClassName="stage-preview-card-popover"
          content={membersPopover.map((item) => (
            <StageMember
              key={item.email}
              member={item}
              isOwner={item.email === ownerEmail}
              isShowPriorityText={false}
            />
          ))}
          placement="topLeft"
        >
          <div className="stage-preview-card_other-members">
            <ApprovalUsersIcon />
            And {membersPopover.length} more
          </div>
        </Popover>
      )}
    </div>
  );
}
